import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Chargement, MessageErreur, Pagination, Pastille } from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { FormulaireInscription } from '../components/inscriptions/FormulaireInscription';
import { useEcranEtroit } from '../lib/useEcranEtroit';
import { Icone } from '../components/Icone';
import type { NomIcone } from '../config/icons';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import {
  urlCv,
  useApprenants,
  type LigneApprenant,
  type LigneCandidatPlacement,
  type ModeDossier,
} from '../lib/useApprenants';
import { VisionneusePiece } from '../components/dossier/VisionneusePiece';
import { useReferentiels } from '../lib/useDocuments';
import { useSites } from '../lib/useSites';
import { urlExportSelection } from '../lib/useExport';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useAuthOptionnel } from '../auth/useAuth';
import { getLearnersViewConfig } from '../config/learners-view';

type Affichage = 'liste' | 'grille';

/**
 * Écran « Apprenants » (`learners` de la maquette validée,
 * `docs/maquette/modules/05-pages-gestion-communication.js`).
 *
 * Structure, classes et dimensions reprises de la maquette : en-tête à trois
 * actions, cinq indicateurs `grid-cols-2 lg:grid-cols-5`, barre de filtres avec
 * bascule d'affichage, tableau dans son `overflow-x-auto` avec
 * vignette en dégradé, barre de progression et menu de ligne, pied de
 * pagination, bandeau d'alerte en bas.
 *
 * Les colonnes de la maquette portent ici les données réelles de
 * TOP FORMATION, sans en inventer aucune :
 *
 * - « Progression » → complétude du dossier, pièces déposées sur attendues ;
 * - « Financeur »   → OPCO de l'entreprise du contrat en cours, plus l'antenne ;
 * - « Statut »      → statut du dossier, avec la minorité et la RQTH.
 *
 * La colonne « Moy. » n'est pas reprise : la plateforme ne tient aucune note
 * à y mettre.
 *
 * Trois écarts assumés avec la maquette, et leurs raisons :
 *
 * 1. Le bandeau bas annonçait une détection de décrochage par IA. Aucun
 *    prestataire d'IA n'est retenu ; le bandeau garde sa forme et porte le
 *    dispositif réel — dossiers incomplets, relance à 15 jours, alertes.
 * 2. La barre de sites est ajoutée : le multi-sites est structurant ici, il ne
 *    l'était pas dans cet écran de la maquette.
 * 3. Un bouton « Réinitialiser » apparaît quand un filtre est actif : la
 *    maquette avait trois listes figées, celle du statut en compte douze.
 *
 * Le périmètre de sites et le cloisonnement viennent de l'API : la liste
 * renvoyée est déjà bornée, le front n'a rien à masquer lui-même.
 */
export function LearnersPage(): JSX.Element {
  const { site } = usePerimetre();
  const naviguer = useNavigate();
  const { data: referentiels } = useReferentiels();
  const { data: sites } = useSites();
  const auth = useAuthOptionnel();
  const utilisateur = auth?.utilisateur;
  const peutAcceder = auth ? auth.peutAcceder : ((_ecran: string) => true);
  const config = getLearnersViewConfig(utilisateur?.profil);
  const vuePlacement = utilisateur?.vueCandidat === 'PLACEMENT';
  const [cvConsulte, setCvConsulte] = useState<LigneCandidatPlacement | null>(null);

  const [statut, setStatut] = useState('');
  const [mode, setMode] = useState<'' | ModeDossier>('');
  const [formation, setFormation] = useState('');
  const [mineur, setMineur] = useState(false);
  const [rqth, setRqth] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [page, setPage] = useState(1);
  // Le tableau réclame 884 px : sur un téléphone il n'en montre que la
  // première colonne. On y ouvre donc les cartes, la bascule restant à portée.
  const ecranEtroit = useEcranEtroit();
  const [affichage, setAffichage] = useState<Affichage>(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 'grille' : 'liste',
  );
  const [affichageForce, setAffichageForce] = useState(false);
  useEffect(() => {
    if (affichageForce) return;
    setAffichage(ecranEtroit ? 'grille' : 'liste');
  }, [ecranEtroit, affichageForce]);
  const [selection, setSelection] = useState<string[]>([]);
  const [inscriptionOuverte, setInscriptionOuverte] = useState(false);
  const [taillePage, setTaillePage] = useTaillePage();

  // Changer de filtre ou de taille remet à la première page : sinon une
  // recherche qui rend trois résultats affichée depuis la page 4 donne une
  // liste vide. La sélection est vidée dans le même mouvement.
  useEffect(() => {
    setPage(1);
    setSelection([]);
  }, [site, statut, mode, formation, mineur, rqth, recherche, taillePage]);

  const { data, isLoading, error } = useApprenants({
    site,
    page,
    taille: taillePage,
    ...(statut ? { statut } : {}),
    ...(mode ? { mode } : {}),
    ...(formation ? { formation } : {}),
    ...(mineur ? { mineur: true } : {}),
    ...(rqth ? { rqth: true } : {}),
    ...(recherche.trim() ? { recherche: recherche.trim() } : {}),
  });

  const lignesPlacement = data?.vue === 'PLACEMENT' ? data.apprenants : [];
  const lignes = data?.vue === 'PLACEMENT' ? [] : (data?.apprenants ?? []);
  const nombreAffiches = lignes.length + lignesPlacement.length;
  const statuts = data?.statuts ?? [];
  const compteurs = data?.compteurs;
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / taillePage));

  // Effectifs de tête, pris dans la répartition renvoyée par l'API : ce sont
  // les mêmes chiffres que ceux du filtre de statut, pas un second calcul.
  const effectif = (code: string): number =>
    statuts.find((ligne) => ligne.statut === code)?.dossiers ?? 0;
  const portefeuille = statuts.reduce((somme, ligne) => somme + ligne.dossiers, 0);

  const filtresActifs = Boolean(statut || mode || formation || mineur || rqth || recherche.trim());
  // Pas de mémoïsation : au plus 25 lignes par page, et `lignes` est un nouveau
  // tableau à chaque rendu, ce qui rendrait le mémo inopérant.
  const idsAffiches = [...lignes, ...lignesPlacement].map((ligne) => ligne.id);
  const toutSelectionne = idsAffiches.length > 0 && selection.length === idsAffiches.length;

  // Sur « Tous les sites », l'antenne n'est présélectionnée que s'il n'y en a
  // qu'une dans le périmètre : sinon le choix revient à la personne qui saisit.
  const siteParDefaut =
    site !== TOUS_LES_SITES ? site : sites?.sites.length === 1 ? sites.sites[0]!.code : '';

  const reinitialiser = (): void => {
    setStatut('');
    setMode('');
    setFormation('');
    setMineur(false);
    setRqth(false);
    setRecherche('');
  };

  const basculerLigne = (id: string): void =>
    setSelection((actuelle) =>
      actuelle.includes(id) ? actuelle.filter((autre) => autre !== id) : [...actuelle, id],
    );

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 lg:p-6">
      {peutAcceder('adm-inscriptions') && (
        <FormulaireInscription
          ouvert={inscriptionOuverte}
          onFermer={() => setInscriptionOuverte(false)}
          siteParDefaut={siteParDefaut}
          onCree={(id) => {
            setInscriptionOuverte(false);
            // Le dossier s'ouvre aussitôt : c'est là que se complètent les
            // champs laissés vides et que se déposent les pièces.
            naviguer(`/adm-dossier/${id}`);
          }}
        />
      )}

      {/* En-tête adapté au pôle : titre, badge, sous-ligne d'effectifs, actions autorisées */}
      <div className="flex flex-col items-start justify-between gap-4 lg:flex-row lg:items-center">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="font-display text-3xl font-extrabold text-codialis-navy lg:text-4xl">
              {config.titre}
            </h1>
            <span className="rounded-full bg-codialis-navy/5 px-3 py-1 text-xs font-semibold text-codialis-navy">
              {config.badgePole}
            </span>
          </div>
          <p className="mt-1 text-codialis-gray">
            {config.sousTitre({ portefeuille, enFormation: effectif('EN_FORMATION') })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {config.actionsSecondaires.map((action) => (
            <BoutonBarre
              key={action.to}
              icone={action.icone}
              libelle={action.libelle}
              onClick={() => naviguer(action.to)}
            />
          ))}
          {peutAcceder('adm-export') && (
            <BoutonBarre
              icone="exporter"
              libelle="Exporter"
              onClick={() => naviguer('/adm-export')}
            />
          )}
          {peutAcceder('adm-inscriptions') && (
            <button
              type="button"
              onClick={() => setInscriptionOuverte(true)}
              className="flex min-h-[44px] items-center gap-2 rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep"
            >
              <Icone nom="plus" className="h-4 w-4" />
              Inscrire un apprenant
            </button>
          )}
        </div>
      </div>

      <BarreSites />

      {/* Cinq indicateurs adaptés au pôle connecté, grille de la maquette à l'identique. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {config
          .indicateurs({
            portefeuille,
            compteurs,
            effectif,
          })
          .map((ind) => (
            <Indicateur
              key={ind.libelle}
              libelle={ind.libelle}
              valeur={ind.valeur}
              detail={ind.detail}
              ton={ind.ton}
            />
          ))}
      </div>

      {/* Barre de filtres de la maquette : recherche, trois listes, bascule
          d'affichage. Les libellés de statut et les formations viennent de
          l'API — aucun référentiel recopié ici. */}
      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-4 text-sm shadow-card">
        <label className="relative min-w-[200px] flex-1">
          <span className="sr-only">Rechercher un apprenant</span>
          <Icone
            nom="recherche"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray"
          />
          <input
            type="search"
            value={recherche}
            onChange={(evenement) => setRecherche(evenement.target.value)}
            placeholder="Rechercher un apprenant..."
            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
          />
        </label>

        <SelectFiltre
          libelle="Filtrer par formation"
          valeur={formation}
          onChanger={setFormation}
          options={[
            { valeur: '', libelle: 'Toutes les formations' },
            ...(referentiels?.formations ?? []).map((item) => ({
              valeur: item.code,
              libelle: `${item.titre} (${item.code})`,
            })),
          ]}
        />

        <SelectFiltre
          libelle="Filtrer par mode de dossier"
          valeur={mode}
          onChanger={(valeur) => setMode(valeur as '' | ModeDossier)}
          options={[
            { valeur: '', libelle: 'CFA et OF' },
            { valeur: 'CFA', libelle: 'CFA · apprentissage' },
            { valeur: 'OF', libelle: 'OF · session de formation' },
          ]}
        />

        <SelectFiltre
          libelle="Filtrer par statut"
          valeur={statut}
          onChanger={setStatut}
          options={[
            { valeur: '', libelle: 'Tous les statuts' },
            ...statuts.map((ligne) => ({
              valeur: ligne.statut,
              libelle: `${ligne.libelle} (${ligne.dossiers})`,
            })),
          ]}
        />

        {!vuePlacement && (
          <>
            <FiltreBascule libelle="Mineur" actif={mineur} onBasculer={() => setMineur((v) => !v)} />
            <FiltreBascule libelle="RQTH" actif={rqth} onBasculer={() => setRqth((v) => !v)} />
          </>
        )}

        {/* Bascule d'affichage de la maquette. Elle y était décorative ; ici
            elle choisit vraiment entre le tableau et les cartes. */}
        <div className="flex rounded-lg bg-gray-50 p-0.5">
          <BoutonAffichage
            icone="liste"
            libelle="Affichage en tableau"
            actif={affichage === 'liste'}
            onClick={() => {
              setAffichage('liste');
              setAffichageForce(true);
            }}
          />
          <BoutonAffichage
            icone="grille"
            libelle="Affichage en cartes"
            actif={affichage === 'grille'}
            onClick={() => {
              setAffichage('grille');
              setAffichageForce(true);
            }}
          />
        </div>

        {filtresActifs && (
          <button
            type="button"
            onClick={reinitialiser}
            className="min-h-[44px] rounded-lg px-3 text-xs font-semibold text-codialis-sky transition hover:underline"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Tableau */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-card">
        {selection.length > 0 && (
          <BarreSelection
            nombre={selection.length}
            site={site}
            dossiers={selection}
            onVider={() => setSelection([])}
            peutExporter={peutAcceder('adm-export')}
          />
        )}

        {error && (
          <div className="p-4 lg:p-6">
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'La liste des apprenants n’a pas pu être chargée.'
              }
            />
          </div>
        )}

        {isLoading && <Chargement libelle="Chargement des apprenants…" />}

        {!isLoading && !error && nombreAffiches === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray">
            Aucun apprenant ne correspond aux filtres actifs
          </p>
        )}

        {!isLoading && !error && nombreAffiches > 0 && (
          <>
            {/* Tableau de la maquette, colonnes et classes à l'identique. Il
                réclame 884 px et défile dans son propre `overflow-x-auto`,
                comme dans la maquette : la page elle-même ne défile jamais
                latéralement. L'affichage en cartes est là pour qui préfère
                éviter ce défilement sur tablette — c'est ce que sert la
                bascule, décorative dans la maquette. */}
            <div className={affichage === 'liste' ? 'overflow-x-auto' : 'hidden'}>
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-codialis-gray">
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={toutSelectionne}
                        onChange={() => setSelection(toutSelectionne ? [] : idsAffiches)}
                        aria-label="Tout sélectionner"
                        className="rounded accent-codialis-mint"
                      />
                    </th>
                    {config.colonnes.map((colonne) => (
                      <th key={colonne.cle} className="whitespace-nowrap px-4 py-3 text-left">
                        {colonne.libelle}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lignes.map((ligne) => (
                    <LigneTableau
                      key={ligne.id}
                      ligne={ligne}
                      selectionnee={selection.includes(ligne.id)}
                      onBasculer={() => basculerLigne(ligne.id)}
                      masquerSensible={config.masquerFinanceurSensible}
                      peutAcceder={peutAcceder}
                    />
                  ))}
                  {lignesPlacement.map((ligne) => (
                    <LigneTableauPlacement
                      key={ligne.id}
                      ligne={ligne}
                      selectionnee={selection.includes(ligne.id)}
                      onBasculer={() => basculerLigne(ligne.id)}
                      onConsulterCv={() => setCvConsulte(ligne)}
                      peutAcceder={peutAcceder}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <ul
              className={
                affichage === 'grille'
                  ? 'grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3'
                  : 'hidden'
              }
            >
              {lignes.map((ligne) => (
                <CarteApprenant
                  key={ligne.id}
                  ligne={ligne}
                  selectionnee={selection.includes(ligne.id)}
                  onBasculer={() => basculerLigne(ligne.id)}
                  masquerSensible={config.masquerFinanceurSensible}
                  peutAcceder={peutAcceder}
                />
              ))}
              {lignesPlacement.map((ligne) => (
                <CartePlacement
                  key={ligne.id}
                  ligne={ligne}
                  selectionnee={selection.includes(ligne.id)}
                  onBasculer={() => basculerLigne(ligne.id)}
                  onConsulterCv={() => setCvConsulte(ligne)}
                  peutAcceder={peutAcceder}
                />
              ))}
            </ul>
          </>
        )}

        {!error && !isLoading && nombreAffiches > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={nombreAffiches}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="apprenant"
          />
        )}
      </div>

      {/* Bandeau bas configuré selon le pôle */}
      <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-red-50 via-amber-50 to-yellow-50 p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-500/10 text-red-600">
            <Icone nom="triangleAlerte" className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h2 className="font-display font-bold text-codialis-navy">
                {config.bandeauBas.titre}
              </h2>
              <Pastille
                texte={config.bandeauBas.badgeTexte(compteurs, { effectif })}
                ton={config.bandeauBas.badgeTon}
              />
            </div>
            <p className="mb-3 text-sm text-codialis-gray">
              {config.bandeauBas.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {config.bandeauBas.boutonAction && (
                <button
                  type="button"
                  onClick={() => setStatut(config.bandeauBas.boutonAction!.statutFiltre)}
                  className="min-h-[40px] rounded-lg bg-codialis-navy px-4 text-xs font-semibold text-white transition hover:bg-codialis-deep"
                >
                  {config.bandeauBas.boutonAction.libelle}
                </button>
              )}
              {config.bandeauBas.liensSecondaires?.map((lien) =>
                lien.mode === 'bouton' ? (
                  <Link
                    key={lien.to}
                    to={lien.to}
                    className="inline-flex min-h-[40px] items-center rounded-lg border border-gray-200 bg-white px-4 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
                  >
                    {lien.libelle}
                  </Link>
                ) : (
                  <Link
                    key={lien.to}
                    to={lien.to}
                    className="inline-flex min-h-[40px] items-center px-2 text-xs font-medium text-codialis-sky transition hover:underline"
                  >
                    {lien.libelle}
                  </Link>
                ),
              )}
            </div>
          </div>
        </div>
      </div>

      {cvConsulte?.cv && (
        <VisionneusePiece
          ouverte
          libellePiece={`CV · ${cvConsulte.prenom} ${cvConsulte.nom}`}
          fichiers={cvConsulte.cv.fichiers}
          urlFichier={(fichierId) => urlCv(cvConsulte.cv!.pieceId, fichierId)}
          onFermer={() => setCvConsulte(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Éléments de la barre d'actions et des filtres
// ---------------------------------------------------------------------------

function BoutonBarre({
  icone,
  libelle,
  onClick,
}: {
  icone: NomIcone;
  libelle: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy transition hover:bg-gray-50"
    >
      <Icone nom={icone} className="h-4 w-4" />
      {libelle}
    </button>
  );
}

function Indicateur({
  libelle,
  valeur,
  detail,
  ton = 'text-codialis-navy',
}: {
  libelle: string;
  valeur: string | number;
  detail: string;
  ton?: string;
}): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-card">
      <div className="text-xs uppercase tracking-wide text-codialis-gray">{libelle}</div>
      <div className={`font-display mt-1 text-3xl font-extrabold ${ton}`}>{valeur}</div>
      <div className="mt-1 text-xs text-codialis-gray">{detail}</div>
    </div>
  );
}

function SelectFiltre({
  libelle,
  valeur,
  onChanger,
  options,
}: {
  libelle: string;
  valeur: string;
  onChanger: (valeur: string) => void;
  options: { valeur: string; libelle: string }[];
}): JSX.Element {
  return (
    <label className="block">
      <span className="sr-only">{libelle}</span>
      <select
        value={valeur}
        onChange={(evenement) => onChanger(evenement.target.value)}
        className="max-w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
      >
        {options.map((option) => (
          <option key={option.valeur} value={option.valeur}>
            {option.libelle}
          </option>
        ))}
      </select>
    </label>
  );
}

function FiltreBascule({
  libelle,
  actif,
  onBasculer,
}: {
  libelle: string;
  actif: boolean;
  onBasculer: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onBasculer}
      aria-pressed={actif}
      className={`min-h-[44px] rounded-lg px-3 text-xs font-semibold transition ${
        actif
          ? 'bg-codialis-navy text-white'
          : 'border border-gray-200 bg-white text-codialis-gray hover:bg-gray-50'
      }`}
    >
      {libelle}
    </button>
  );
}

function BoutonAffichage({
  icone,
  libelle,
  actif,
  onClick,
}: {
  icone: NomIcone;
  libelle: string;
  actif: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={actif}
      title={libelle}
      className={`rounded-md px-3 py-2.5 transition ${
        actif ? 'bg-white text-codialis-navy shadow' : 'text-codialis-gray hover:text-codialis-navy'
      }`}
    >
      <span className="sr-only">{libelle}</span>
      <Icone nom={icone} className="h-4 w-4" />
    </button>
  );
}

/**
 * Barre d'actions groupées, visible dès qu'une ligne est cochée. L'export d'une
 * sélection n'est proposé qu'aux profils qui ont l'écran d'export : l'API le
 * refuserait de toute façon, et un bouton qui échoue ne vaut rien.
 */
function BarreSelection({
  nombre,
  site,
  dossiers,
  onVider,
  peutExporter = true,
}: {
  nombre: number;
  site: string;
  dossiers: string[];
  onVider: () => void;
  peutExporter?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col items-start gap-3 border-b border-gray-100 bg-codialis-sand px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm font-semibold text-codialis-navy">
        {nombre} dossier{nombre > 1 ? 's' : ''} sélectionné{nombre > 1 ? 's' : ''}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {peutExporter && (
          <a
            href={urlExportSelection(site, dossiers)}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-codialis-navy px-4 text-xs font-semibold text-white transition hover:bg-codialis-deep"
          >
            <Icone nom="telecharger" className="h-4 w-4" />
            Exporter la sélection (CSV)
          </a>
        )}
        <button
          type="button"
          onClick={onVider}
          className="min-h-[40px] rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
        >
          Annuler la sélection
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contenu commun aux deux affichages
// ---------------------------------------------------------------------------

/**
 * Dégradé de la vignette. La maquette en donnait un par apprenant, écrit à la
 * main ; ici il est dérivé du nom, de sorte que la même personne garde toujours
 * la même couleur d'une page à l'autre.
 */
const DEGRADES = [
  'from-codialis-sky to-codialis-blue',
  'from-codialis-mint to-codialis-teal',
  'from-violet-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-cyan-500 to-blue-600',
] as const;

type IdentiteLigne = Pick<LigneApprenant, 'id' | 'nom' | 'prenom' | 'initiales'>;

function degrade(ligne: IdentiteLigne): string {
  let somme = 0;
  const cle = `${ligne.nom}${ligne.prenom}`;
  for (let index = 0; index < cle.length; index += 1) somme += cle.charCodeAt(index);
  return DEGRADES[somme % DEGRADES.length]!;
}

function Vignette({ ligne, taille }: { ligne: IdentiteLigne; taille: string }): JSX.Element {
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${degrade(
        ligne,
      )} font-bold text-white ${taille}`}
    >
      {ligne.initiales}
    </div>
  );
}

/**
 * Complétude du dossier, à la place de la progression pédagogique de la
 * maquette : c'est l'avancement qui existe réellement à ce stade du parcours.
 */
function BarreCompletude({ ligne }: { ligne: LigneApprenant }): JSX.Element {
  const attendues = ligne.pieces.attendues;
  const pourcentage =
    attendues === 0 ? 0 : Math.round((ligne.pieces.deposees / attendues) * 100);
  const complet = pourcentage === 100;
  return (
    <div className="flex items-center gap-2">
      <div className="progress-track h-1.5 w-24 overflow-hidden rounded-full">
        <div
          className={`h-full rounded-full ${complet ? 'bg-codialis-mint' : 'progress-bar'}`}
          style={{ width: `${pourcentage}%` }}
        />
      </div>
      <span className="text-xs font-semibold text-codialis-navy">{pourcentage}%</span>
    </div>
  );
}

function Situation({ ligne }: { ligne: LigneApprenant }): JSX.Element | null {
  if (!ligne.mineur && !ligne.rqth) return null;
  return (
    <>
      {ligne.mineur && <Pastille texte="Mineur" ton="warning" />}
      {ligne.rqth && <Pastille texte="RQTH" ton="info" />}
    </>
  );
}

/**
 * Colonne « Financeur » de la maquette. Elle y portait un badge CPF ou OPCO
 * écrit à la main ; ici c'est l'OPCO de l'entreprise du contrat en cours, avec
 * le mode de dossier et l'antenne, seuls éléments de prise en charge connus
 * tant qu'aucun contrat n'est signé. Pour les formateurs, les données financières
 * sensibles sont masquées au profit du mode et du site.
 */
function Financeur({
  ligne,
  masquerSensible,
}: {
  ligne: LigneApprenant;
  masquerSensible?: boolean;
}): JSX.Element {
  if (masquerSensible) {
    return (
      <div className="flex flex-wrap items-center gap-1">
        <Pastille texte={ligne.mode === 'CFA' ? 'CFA' : 'OF'} ton="neutral" />
        <Pastille texte={ligne.siteCode} ton="mint" />
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {ligne.opco ? (
        <Pastille texte={ligne.opco} ton="info" />
      ) : (
        <Pastille texte={ligne.mode === 'CFA' ? 'OPCO à définir' : 'Session OF'} ton="neutral" />
      )}
      <Pastille texte={ligne.siteCode} ton="mint" />
    </div>
  );
}

/** Actions de ligne adaptées aux droits du pôle connecté. */
function ActionsLigne({
  ligne,
  peutAcceder,
}: {
  ligne: IdentiteLigne;
  peutAcceder: (ecran: string) => boolean;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);

  // Fermeture au clic ailleurs et à Échap, comme tout menu de l'application.
  useEffect(() => {
    if (!ouvert) return undefined;
    const fermer = (): void => setOuvert(false);
    const auClavier = (evenement: KeyboardEvent): void => {
      if (evenement.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('click', fermer);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('click', fermer);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  const entrees: { to: string; libelle: string; icone: NomIcone }[] = [
    { to: `/adm-dossier/${ligne.id}`, libelle: 'Ouvrir le dossier', icone: 'dossier' },
    // Droit au bloc Financement du dossier (TF-464), sans chercher plus bas.
    ...(peutAcceder('financing')
      ? [{ to: `/adm-dossier/${ligne.id}#financement`, libelle: 'Financement', icone: 'euro' as NomIcone }]
      : []),
    ...(peutAcceder('adm-documents')
      ? [
          {
            to: `/adm-documents/${ligne.id}`,
            libelle: 'Documents réglementaires',
            icone: 'document' as NomIcone,
          },
        ]
      : []),
    ...(peutAcceder('com-relances')
      ? [{ to: '/com-relances', libelle: 'Relances mail / SMS', icone: 'message' as NomIcone }]
      : []),
    ...(peutAcceder('alertes')
      ? [{ to: '/alertes', libelle: 'Alertes du périmètre', icone: 'cloche' as NomIcone }]
      : []),
    ...(peutAcceder('com-entretiens')
      ? [{ to: '/com-entretiens', libelle: 'Planning des entretiens', icone: 'calendrier' as NomIcone }]
      : []),
    ...(peutAcceder('com-matching')
      ? [{ to: '/com-matching', libelle: 'Matching entreprises', icone: 'cible' as NomIcone }]
      : []),
    ...(peutAcceder('ped-sessions')
      ? [{ to: '/ped-sessions', libelle: 'Sessions de formation', icone: 'parcours' as NomIcone }]
      : []),
    ...(peutAcceder('instructor-promos')
      ? [{ to: '/instructor-promos', libelle: 'Mes promotions', icone: 'utilisateurs' as NomIcone }]
      : []),
    ...(peutAcceder('emargement')
      ? [{ to: '/emargement', libelle: 'Feuilles d’émargement', icone: 'stylo' as NomIcone }]
      : []),
  ];

  return (
    <div className="flex items-center justify-end gap-1">
      <Link
        to={`/adm-dossier/${ligne.id}`}
        title="Ouvrir le dossier"
        className="rounded p-1.5 text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
      >
        <span className="sr-only">Ouvrir le dossier de {ligne.prenom} {ligne.nom}</span>
        <Icone nom="oeil" className="h-4 w-4" />
      </Link>
      {peutAcceder('adm-documents') && (
        <Link
          to={`/adm-documents/${ligne.id}`}
          title="Documents réglementaires"
          className="rounded p-1.5 text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
        >
          <span className="sr-only">Documents réglementaires de {ligne.prenom} {ligne.nom}</span>
          <Icone nom="document" className="h-4 w-4" />
        </Link>
      )}
      <div className="relative inline-block">
        <button
          type="button"
          aria-expanded={ouvert}
          title="Plus d’actions"
          onClick={(evenement) => {
            evenement.stopPropagation();
            setOuvert((valeur) => !valeur);
          }}
          className="rounded p-1.5 text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy"
        >
          <span className="sr-only">Plus d’actions</span>
          <Icone nom="points" className="h-4 w-4" />
        </button>
        {ouvert && (
          <div className="absolute right-0 top-full z-30 mt-1 w-56 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 text-left shadow-xl">
            {entrees.map((entree) => (
              <Link
                key={entree.libelle}
                to={entree.to}
                className="flex w-full items-center gap-2.5 px-3 py-2.5 text-xs text-codialis-navy transition hover:bg-gray-50"
              >
                <Icone nom={entree.icone} className="h-4 w-4 text-codialis-gray" />
                {entree.libelle}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LigneTableau({
  ligne,
  selectionnee,
  onBasculer,
  masquerSensible,
  peutAcceder,
}: {
  ligne: LigneApprenant;
  selectionnee: boolean;
  onBasculer: () => void;
  masquerSensible?: boolean;
  peutAcceder: (ecran: string) => boolean;
}): JSX.Element {
  return (
    <tr className={`transition hover:bg-gray-50 ${selectionnee ? 'bg-codialis-sand/60' : ''}`}>
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectionnee}
          onChange={onBasculer}
          aria-label={`Sélectionner ${ligne.prenom} ${ligne.nom}`}
          className="rounded accent-codialis-mint"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Vignette ligne={ligne} taille="h-9 w-9 text-xs" />
          <div className="min-w-0">
            <div className="truncate font-semibold text-codialis-navy">
              <Link
                to={`/adm-dossier/${ligne.id}`}
                className="cursor-pointer transition hover:text-codialis-sky hover:underline focus:outline-none focus:ring-2 focus:ring-codialis-sky rounded"
              >
                {ligne.prenom} {ligne.nom}
              </Link>
            </div>
            <div className="truncate text-xs text-codialis-gray">
              {ligne.email ?? (ligne.age === null ? 'âge inconnu' : `${ligne.age} ans`)}
            </div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-codialis-navy">{ligne.formation ?? '—'}</td>
      <td className="px-4 py-3">
        <BarreCompletude ligne={ligne} />
      </td>
      <td className="px-4 py-3">
        <Financeur ligne={ligne} masquerSensible={masquerSensible} />
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-1">
          <Pastille texte={ligne.statutLibelle} ton={ligne.statutTon} />
          <Situation ligne={ligne} />
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <ActionsLigne ligne={ligne} peutAcceder={peutAcceder} />
      </td>
    </tr>
  );
}

/** La même ligne repliée en carte, pour le téléphone, la tablette et la grille. */
function CarteApprenant({
  ligne,
  selectionnee,
  onBasculer,
  masquerSensible,
  peutAcceder,
}: {
  ligne: LigneApprenant;
  selectionnee: boolean;
  onBasculer: () => void;
  masquerSensible?: boolean;
  peutAcceder: (ecran: string) => boolean;
}): JSX.Element {
  return (
    <li
      className={`border-b border-gray-100 p-4 transition sm:[&:nth-child(odd)]:border-r ${
        selectionnee ? 'bg-codialis-sand/60' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selectionnee}
          onChange={onBasculer}
          aria-label={`Sélectionner ${ligne.prenom} ${ligne.nom}`}
          className="mt-1 rounded accent-codialis-mint"
        />
        <Vignette ligne={ligne} taille="h-10 w-10 text-sm" />
        <div className="min-w-0 flex-1">
          <Link
            to={`/adm-dossier/${ligne.id}`}
            className="block truncate font-semibold text-codialis-navy cursor-pointer transition hover:text-codialis-sky hover:underline focus:outline-none focus:ring-2 focus:ring-codialis-sky rounded"
          >
            {ligne.prenom} {ligne.nom}
          </Link>
          <div className="truncate text-xs text-codialis-gray">
            {ligne.email ?? (ligne.age === null ? 'âge inconnu' : `${ligne.age} ans`)}
          </div>
        </div>
        <Pastille texte={ligne.statutLibelle} ton={ligne.statutTon} />
      </div>

      <div className="mt-3 text-sm text-codialis-navy">{ligne.formation ?? '—'}</div>

      <div className="mt-2 flex items-center justify-between gap-3">
        <BarreCompletude ligne={ligne} />
        <span className="whitespace-nowrap text-xs font-semibold text-codialis-navy">
          {ligne.pieces.deposees}/{ligne.pieces.attendues} pièces
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <Financeur ligne={ligne} masquerSensible={masquerSensible} />
          <Situation ligne={ligne} />
        </div>
        <ActionsLigne ligne={ligne} peutAcceder={peutAcceder} />
      </div>
    </li>
  );
}

function BoutonCv({
  ligne,
  onConsulter,
}: {
  ligne: LigneCandidatPlacement;
  onConsulter: () => void;
}): JSX.Element {
  if (!ligne.cv) {
    return <span className="text-xs text-codialis-gray">Aucun CV</span>;
  }
  return (
    <button
      type="button"
      onClick={onConsulter}
      aria-label={`Voir le CV de ${ligne.prenom} ${ligne.nom}`}
      className="inline-flex min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] items-center gap-1.5 rounded-lg border border-codialis-navy/20 px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-codialis-sky"
    >
      <Icone nom="document" className="h-3.5 w-3.5" />
      Voir le CV
    </button>
  );
}

function LienCandidat({ ligne, className }: { ligne: IdentiteLigne; className: string }): JSX.Element {
  return (
    <Link to={`/adm-dossier/${ligne.id}`} className={className}>
      {ligne.prenom} {ligne.nom}
    </Link>
  );
}

function LigneTableauPlacement({
  ligne,
  selectionnee,
  onBasculer,
  onConsulterCv,
  peutAcceder,
}: {
  ligne: LigneCandidatPlacement;
  selectionnee: boolean;
  onBasculer: () => void;
  onConsulterCv: () => void;
  peutAcceder: (ecran: string) => boolean;
}): JSX.Element {
  return (
    <tr className={`transition hover:bg-gray-50 ${selectionnee ? 'bg-codialis-sand/60' : ''}`}>
      <td className="px-4 py-3">
        <input
          type="checkbox"
          checked={selectionnee}
          onChange={onBasculer}
          aria-label={`Sélectionner ${ligne.prenom} ${ligne.nom}`}
          className="rounded accent-codialis-mint"
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Vignette ligne={ligne} taille="h-9 w-9 text-xs" />
          <div className="min-w-0 truncate font-semibold text-codialis-navy">
            <LienCandidat
              ligne={ligne}
              className="cursor-pointer rounded transition hover:text-codialis-sky hover:underline focus:outline-none focus:ring-2 focus:ring-codialis-sky"
            />
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-codialis-navy">{ligne.formation ?? '—'}</td>
      <td className="px-4 py-3">
        <BoutonCv ligne={ligne} onConsulter={onConsulterCv} />
      </td>
      <td className="px-4 py-3">
        <Pastille texte={ligne.statutLibelle} ton={ligne.statutTon} />
      </td>
      <td className="px-4 py-3 text-right">
        <ActionsLigne ligne={ligne} peutAcceder={peutAcceder} />
      </td>
    </tr>
  );
}

function CartePlacement({
  ligne,
  selectionnee,
  onBasculer,
  onConsulterCv,
  peutAcceder,
}: {
  ligne: LigneCandidatPlacement;
  selectionnee: boolean;
  onBasculer: () => void;
  onConsulterCv: () => void;
  peutAcceder: (ecran: string) => boolean;
}): JSX.Element {
  return (
    <li
      className={`border-b border-gray-100 p-4 transition sm:[&:nth-child(odd)]:border-r ${
        selectionnee ? 'bg-codialis-sand/60' : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selectionnee}
          onChange={onBasculer}
          aria-label={`Sélectionner ${ligne.prenom} ${ligne.nom}`}
          className="mt-1 rounded accent-codialis-mint"
        />
        <Vignette ligne={ligne} taille="h-10 w-10 text-sm" />
        <div className="min-w-0 flex-1">
          <LienCandidat
            ligne={ligne}
            className="block cursor-pointer truncate rounded font-semibold text-codialis-navy transition hover:text-codialis-sky hover:underline focus:outline-none focus:ring-2 focus:ring-codialis-sky"
          />
        </div>
        <Pastille texte={ligne.statutLibelle} ton={ligne.statutTon} />
      </div>

      <div className="mt-3 text-sm text-codialis-navy">{ligne.formation ?? '—'}</div>

      <div className="mt-3 flex items-end justify-between gap-2">
        <BoutonCv ligne={ligne} onConsulter={onConsulterCv} />
        <ActionsLigne ligne={ligne} peutAcceder={peutAcceder} />
      </div>
    </li>
  );
}
